import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import CustomerReceipt from "../components/CustomerReceipt";
import GoogleSignInButton from "../components/GoogleSignInButton";
import BrandLogo from "../components/BrandLogo";
import { SKIP_ADMIN_LOGIN, UI_PREVIEW_MODE, USE_FIREBASE_AUTH, isAdminEmail } from "../config";
import { loadPreviewCustomers } from "../previewStore";
import { useAuth } from "../useAuth";
import { apiRequest } from "../utils";
import { downloadReceiptPdf } from "../utils/downloadReceiptPdf";

export default function AdminReceiptPage() {
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const receiptRef = useRef(null);
  const { user, loading: authLoading, signInWithGoogle, getToken } = useAuth();
  const preloadedCustomer =
    location.state?.customer?.customerId === customerId ? location.state.customer : null;
  const [customer, setCustomer] = useState(preloadedCustomer);
  const [loading, setLoading] = useState(!preloadedCustomer);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const isAdmin = SKIP_ADMIN_LOGIN || (USE_FIREBASE_AUTH && user && (UI_PREVIEW_MODE ? isAdminEmail(user.email) : true));

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        if (UI_PREVIEW_MODE) {
          const found = loadPreviewCustomers().find((c) => c.customerId === customerId);
          if (!found) throw new Error("Application not found.");
          if (!cancelled) setCustomer(found);
          return;
        }

        const token = await getToken();
        const data = await apiRequest(`/api/admin/customers/${customerId}`, { method: "GET" }, token);
        if (!cancelled) setCustomer(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setCustomer(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [authLoading, isAdmin, customerId, getToken]);

  const handleDownload = async () => {
    if (!customer || !receiptRef.current) return;
    setDownloading(true);
    try {
      const safeName = (customer.name || customer.customerId).replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-");
      await downloadReceiptPdf(receiptRef.current, `Square-One-Estates-Receipt-${safeName}.pdf`);
    } catch {
      setError("Could not generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="portal-page">
        <div className="portal-loading">
          <BrandLogo size="xl" />
          <p>Loading receipt…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <p className="admin-eyebrow">Staff Access</p>
          <h2>Admin Login Required</h2>
          <p className="portal-login-note">Sign in to view application receipts.</p>
          {USE_FIREBASE_AUTH ? (
            <GoogleSignInButton onClick={signInWithGoogle} label="Sign in with Google" />
          ) : (
            <Link to="/admin" className="admin-btn admin-btn--primary">Go to Admin</Link>
          )}
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="admin-page">
        <div className="admin-login-card">
          <p className="admin-error">{error || "Application not found."}</p>
          <button type="button" className="admin-btn admin-btn--ghost" onClick={() => navigate("/admin")}>
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="receipt-toolbar">
        <div className="receipt-toolbar__inner">
          <button type="button" className="admin-btn admin-btn--ghost" onClick={() => navigate("/admin")}>
            ← Back to Admin
          </button>
          <div className="receipt-toolbar__actions">
            <span className="receipt-toolbar__meta">{customer.customerId} · {customer.name}</span>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Preparing PDF…" : "Download PDF"}
            </button>
          </div>
        </div>
      </div>
      <div ref={receiptRef}>
        <CustomerReceipt customer={customer} />
      </div>
    </>
  );
}
